/**
 * Whether a roof template is finished enough to estimate from.
 *
 * Twenty-one of the twenty-nine templates in the catalogue are stubs. Nine hold
 * nothing at all; the rest hold only lines that blanket migrations added to
 * every template at once — `Shingle Base Labor`, `OSB Roof Decking - Labor`,
 * the Cobra ridge vent. Picking one generates an estimate with three lines on
 * it and a margin that means nothing.
 *
 * Jonathon, 2026-08-09: "I will build them out", and "For the empty templates
 * just make it obvoius that they're in building phase."
 *
 * So this reads a flag rather than counting lines. The counting version is the
 * obvious implementation and it is wrong in the direction that matters: a
 * template halfway through being built would cross whatever threshold was
 * chosen and stop warning, which is precisely when a wrong estimate gets
 * written. The owner is the one doing the building, so he is the one who says
 * when a template is done — `roof_templates.built_out`, migration
 * 20260809120000.
 */

/** The parts of a template this needs. Anything with these fields will do. */
export interface TemplateReadiness {
  built_out?: boolean | null;
}

/**
 * True when the template is still being built and should be shown as such.
 *
 * Absent or null counts as unbuilt. A template that has never been looked at is
 * far more likely to be a stub than a finished one, and the failure directions
 * are not equal: a spurious badge is a moment's confusion, while a missing one
 * is an estimate sent to a customer with three lines on it.
 */
export function templateIsUnbuilt(template: TemplateReadiness): boolean {
  return template.built_out !== true;
}

/** What to call it on screen. One string, so every surface agrees. */
export const UNBUILT_TEMPLATE_LABEL = "Still being built";

/** The longer form, for a tooltip or a banner. */
export const UNBUILT_TEMPLATE_EXPLANATION =
  "This template has not been filled in yet, so an estimate generated from it " +
  "will be missing most of its lines.";

/**
 * The paths a replacement is sold down, and the tiers on each.
 *
 * Jonathon, 2026-08-17: every replacement offers **shingle or metal** first,
 * and then the tiers in this order. Bulletproof is the same template on both —
 * GAF TimberSteel is a metal shingle, so it is the top of both lists and one
 * price either way.
 *
 * Matched on `package_name`, which is what the customer reads, rather than on
 * the template's own name, which is what the office calls it and changes.
 *
 * Flat & Other is not a third way of selling a roof and is not tiered. It is
 * where a template goes when it is not one of the two — a TPO reroof, a PVC
 * membrane — so that "built out" always means "reachable". See the note in
 * `templatesForPath`.
 */
export const ROOF_PATHS = [
  "Shingle",
  "Metal",
  "Flat & Other",
  "Siding",
  "Decks",
] as const;
export type RoofPath = (typeof ROOF_PATHS)[number];

export const PATH_TIERS: Record<RoofPath, readonly string[]> = {
  Shingle: [
    "Bulletproof Package",
    "Peace of Mind Package",
    "Essentials Package",
    "Standard Architectural Shingles",
    "Bare Bones Package",
  ],
  Metal: [
    "Bulletproof Package",
    "Peace of Mind Package",
    "Essentials Package",
    // Jonathon, 2026-09-09, on where the 29 gauge panel sits: "it's a cheaper
    // option that essentials. let's call it an economy option." The same roof
    // as Essentials in a thinner panel, so it belongs below it rather than
    // beside it.
    "Economy Package",
  ],
  // No tiers. A flat roof is not sold as good/better/best, and the templates
  // that land here are whatever does not fit the two selling paths.
  "Flat & Other": [],
  // Nor is siding, yet. One template, measured on the walls (siding.ts).
  Siding: [],
  // Pressure treated and Trex are two materials, not two tiers.
  Decks: [],
};

/** The roof types each path sells. TimberSteel is a metal shingle, hence both. */
const PATH_ROOF_TYPES: Record<RoofPath, readonly string[]> = {
  Shingle: ["shingle", "metal shingle"],
  Metal: [
    "metal shingle",
    "standing seam metal",
    "exposed fastener metal panel",
    "ag panel 26 gauge residential",
    "r panel 26 gauge residential",
  ],
  // Named for what it is, but membership is decided by exclusion below: a roof
  // type nobody has listed belongs here rather than nowhere.
  "Flat & Other": ["flat / low slope"],
  // Not a roof, but routed by the same column: a wall is one more surface a
  // template is measured on. Jonathon, 2026-09-30: "there needs to be a
  // template for that."
  Siding: ["siding"],
  // Not a roof either. Jonathon, 2026-10-11: "there needs to be a pressure
  // treated template and a trex template." See deck.ts.
  Decks: ["deck", "trex deck"],
};

/**
 * Every roof type a named path claims, lower-cased. Flat & Other takes what
 * is left, so it is the one path not listed here.
 */
const SOLD_ROOF_TYPES: readonly string[] = [
  ...PATH_ROOF_TYPES.Shingle,
  ...PATH_ROOF_TYPES.Metal,
  ...PATH_ROOF_TYPES.Siding,
  ...PATH_ROOF_TYPES.Decks,
];

/** The little of a template that choosing between them depends on. */
export interface PathTemplate {
  name: string;
  roof_type?: string | null;
  package_name?: string | null;
  built_out?: boolean | null;
}

/**
 * The templates a path offers, in tier order.
 *
 * **A template with no products is not offered.** Jonathon, 2026-08-17: "any
 * template with no products is hidden from the estimate dropdown" — they stay
 * visible in Tools, which is where they are built. That reverses the August
 * decision to badge them instead, and it is the right way round now that the
 * list is a list of packages rather than of everything in the catalogue.
 *
 * Anything on the path that is not one of the tiers follows them, so a
 * template nobody has given a package name to is still reachable rather than
 * quietly lost.
 */
export function templatesForPath<T extends PathTemplate>(
  templates: readonly T[],
  path: RoofPath,
): T[] {
  const tiers = PATH_TIERS[path];
  const rank = (template: T) => {
    const at = tiers.indexOf((template.package_name ?? "").trim());
    return at === -1 ? tiers.length : at;
  };
  /*
   * A template on a roof type neither selling path claims lands on Flat &
   * Other rather than nowhere.
   *
   * Until 2026-08-24 it landed nowhere. Shingle and Metal each named the roof
   * types they sold and anything else — the TPO reroof, the PVC, whatever gets
   * added next — was built, marked built, and unreachable from the estimate
   * screen, with nothing on the screen to say so. Samuel met it from the
   * commercial side, where flat roofs are most of the work, and read it as the
   * job type deciding which templates he was allowed (Jonathon, 2026-08-24:
   * "the job type should not affect the estimate templates that are
   * available"). It never did and still does not — `job_type` is not an input
   * here and there is a test that says so. What was true is that a template
   * could be hidden with no way to reach it, and that is fixed by giving the
   * leftovers a home instead of by widening the two paths that are sold.
   */
  const belongs = (template: T) => templateIsOnPath(template, path);
  return templates
    .filter((template) => !templateIsUnbuilt(template))
    .filter(belongs)
    .sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
}

/**
 * Whether a template is sold down a path, built or not. A roof type neither
 * selling path claims belongs to Flat & Other (see `templatesForPath`).
 */
export function templateIsOnPath(
  template: Pick<PathTemplate, "roof_type">,
  path: RoofPath,
): boolean {
  const type = (template.roof_type ?? "").trim().toLowerCase();
  if (PATH_ROOF_TYPES[path].includes(type)) return true;
  return path === "Flat & Other" && !SOLD_ROOF_TYPES.includes(type);
}

/**
 * The one path a template is bought on: the first that sells it.
 *
 * A metal shingle (GAF TimberSteel) is on both Shingle and Metal and lands on
 * Shingle, because it is a shingle manufacturer's product bought where the
 * shingles are.
 */
export function purchasePathOf(
  template: Pick<PathTemplate, "roof_type">,
): RoofPath {
  // Every roof type lands somewhere: Flat & Other takes what no path claims.
  return (
    ROOF_PATHS.find((path) => templateIsOnPath(template, path)) ??
    "Flat & Other"
  );
}
